import { useState, type ChangeEvent, type DragEvent } from 'react';
import { useAuth } from '../auth/AuthContext';

interface UploadedFile {
  deal_id: string;
  deal_number?: string;
  document_id: string;
  filename: string;
  sha256: string;
  bytes: number;
  s3_url: string;
}

interface SingleResponse extends UploadedFile {
  request_id: string;
  mime: string;
}

interface BatchResponse {
  batch_id: string;
  count: number;
  total_bytes: number;
  files: UploadedFile[];
}

const ALLOWED_EXT = ['.xlsx', '.xls', '.csv', '.pdf', '.jpg', '.jpeg', '.png'];
const MAX_SINGLE_MB = 50;
const MAX_BATCH_FILES = 20;
const MAX_BATCH_TOTAL_MB = 200;

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function extOf(name: string): string {
  const m = name.toLowerCase().match(/\.[a-z0-9]+$/);
  return m ? m[0] : '';
}

export function UploadPage(): JSX.Element {
  const { user, token, logout } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadedFile[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // RequireAuth gate guarantees user & token are present here.
  if (!user || !token) return <></>;

  const validateFiles = (selected: FileList | File[]): { ok: File[]; err: string | null } => {
    const arr = Array.from(selected);
    if (arr.length > MAX_BATCH_FILES) {
      return { ok: [], err: `Максимум ${MAX_BATCH_FILES} файлов за раз` };
    }
    let total = 0;
    for (const f of arr) {
      const ext = extOf(f.name);
      if (!ALLOWED_EXT.includes(ext)) {
        return { ok: [], err: `Файл "${f.name}": расширение ${ext || '?'} не поддерживается. Разрешены: ${ALLOWED_EXT.join(', ')}` };
      }
      if (f.size > MAX_SINGLE_MB * 1024 * 1024) {
        return { ok: [], err: `Файл "${f.name}" больше ${MAX_SINGLE_MB} MB` };
      }
      total += f.size;
    }
    if (total > MAX_BATCH_TOTAL_MB * 1024 * 1024) {
      return { ok: [], err: `Общий размер ${fmtBytes(total)} превышает ${MAX_BATCH_TOTAL_MB} MB` };
    }
    return { ok: arr, err: null };
  };

  const onFileSelect = (e: ChangeEvent<HTMLInputElement>): void => {
    setError(null);
    setResult([]);
    if (!e.target.files) return;
    const { ok, err } = validateFiles(e.target.files);
    if (err) {
      setError(err);
      return;
    }
    setFiles(ok);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragOver(false);
    setError(null);
    setResult([]);
    const { ok, err } = validateFiles(e.dataTransfer.files);
    if (err) {
      setError(err);
      return;
    }
    setFiles(ok);
  };

  const upload = async (): Promise<void> => {
    if (files.length === 0) {
      setError('Выберите хотя бы один файл');
      return;
    }
    setBusy(true);
    setError(null);
    setProgress(0);

    try {
      if (files.length === 1) {
        const only = files[0];
        if (!only) throw new Error('No file');
        await uploadSingle(only);
      } else {
        await uploadBatch(files);
      }
      setFiles([]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const uploadSingle = async (file: File): Promise<void> => {
    const form = new FormData();
    form.append('file', file);

    const data = await xhrUpload<SingleResponse>('/api/v1/uploads/single', form, (p) => setProgress(p));
    setResult([
      {
        deal_id: data.deal_id,
        deal_number: data.deal_number,
        document_id: data.document_id,
        filename: data.filename,
        sha256: data.sha256,
        bytes: data.bytes,
        s3_url: data.s3_url,
      },
    ]);
  };

  const uploadBatch = async (selected: File[]): Promise<void> => {
    const form = new FormData();
    for (const f of selected) {
      form.append('file', f);
    }
    const data = await xhrUpload<BatchResponse>('/api/v1/uploads/batch', form, (p) => setProgress(p));
    setResult(data.files);
  };

  const xhrUpload = <T,>(
    url: string,
    form: FormData,
    onProgress: (p: number) => void,
  ): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        const text = xhr.responseText;
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(text) as T);
          } catch {
            reject(new Error('Bad JSON response'));
          }
        } else if (xhr.status === 401) {
          logout();
          reject(new Error('Сессия истекла, войдите снова'));
        } else {
          let msg = `HTTP ${xhr.status}`;
          try {
            const body = JSON.parse(text) as { message?: string; error?: string };
            msg = body.message ?? body.error ?? msg;
          } catch {
            /* keep msg */
          }
          reject(new Error(msg));
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(form);
    });
  };

  return (
    <div className="space-y-6">
      <section className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-ntr-primary/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-ntr-primary">Загрузка файлов</h2>
            <p className="mt-1 text-sm text-ntr-muted">
              {user.email} · 1 файл или до {MAX_BATCH_FILES} файлов за раз ·{' '}
              {ALLOWED_EXT.join(', ')} · максимум {MAX_SINGLE_MB} MB / файл,{' '}
              {MAX_BATCH_TOTAL_MB} MB суммарно
            </p>
          </div>
          <button
            onClick={() => logout()}
            className="text-sm text-ntr-muted hover:text-ntr-primary"
          >
            Выйти
          </button>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={
            'mt-6 flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition ' +
            (dragOver
              ? 'border-ntr-primary bg-ntr-primary/5'
              : 'border-ntr-primary/20 bg-gray-50/50')
          }
        >
          <p className="text-base font-medium text-ntr-text">
            Перетащите файлы сюда или нажмите кнопку
          </p>
          <p className="mt-1 text-xs text-ntr-muted">
            XLSX, XLS, CSV, PDF, JPG, PNG
          </p>
          <label className="mt-4 cursor-pointer rounded-md bg-ntr-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-ntr-primary/90">
            Выбрать файлы
            <input
              type="file"
              multiple
              accept={ALLOWED_EXT.join(',')}
              onChange={onFileSelect}
              className="hidden"
            />
          </label>
        </div>

        {files.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-ntr-text">Выбрано: {files.length}</h3>
            <ul className="mt-2 divide-y divide-ntr-primary/10 rounded-md border border-ntr-primary/10 bg-white">
              {files.map((f, i) => (
                <li key={i} className="flex justify-between px-3 py-2 text-sm">
                  <span className="truncate">{f.name}</span>
                  <span className="ml-3 shrink-0 text-ntr-muted">{fmtBytes(f.size)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center justify-between">
              <button
                onClick={upload}
                disabled={busy}
                className="rounded-md bg-ntr-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-ntr-primary/90 disabled:opacity-50"
              >
                {busy ? `Загрузка ${progress}%…` : `Загрузить ${files.length} файл(ов)`}
              </button>
              {!busy && (
                <button
                  onClick={() => setFiles([])}
                  className="text-sm text-ntr-muted hover:text-ntr-primary"
                >
                  Очистить
                </button>
              )}
            </div>
            {busy && (
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ntr-primary/10">
                <div
                  className="h-full bg-ntr-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </section>

      {result.length > 0 && (
        <section className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-green-200">
          <h3 className="text-lg font-semibold text-green-700">
            ✓ Загружено {result.length} файл(ов)
          </h3>
          <ul className="mt-4 space-y-3">
            {result.map((f) => (
              <li
                key={f.document_id}
                className="rounded-md border border-green-100 bg-green-50/30 p-3 text-sm"
              >
                <div className="font-medium text-ntr-text">{f.filename}</div>
                <div className="mt-1 grid grid-cols-1 gap-1 text-xs text-ntr-muted md:grid-cols-2">
                  <div>
                    deal_id:{' '}
                    <code className="rounded bg-white px-1 py-0.5">{f.deal_id}</code>
                  </div>
                  <div>
                    document_id:{' '}
                    <code className="rounded bg-white px-1 py-0.5">{f.document_id}</code>
                  </div>
                  <div>
                    sha256:{' '}
                    <code className="rounded bg-white px-1 py-0.5">
                      {f.sha256.slice(0, 16)}…
                    </code>
                  </div>
                  <div>
                    size: <code className="rounded bg-white px-1 py-0.5">{fmtBytes(f.bytes)}</code>
                  </div>
                </div>
                <div className="mt-2">
                  <a
                    href={`/api/v1/uploads/${f.document_id}/presigned`}
                    onClick={async (e) => {
                      e.preventDefault();
                      const r = await fetch(`/api/v1/uploads/${f.document_id}/presigned`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (r.ok) {
                        const data = (await r.json()) as { url: string };
                        window.open(data.url, '_blank');
                      }
                    }}
                    className="text-xs text-ntr-primary hover:underline"
                  >
                    Скачать
                  </a>
                  {' · '}
                  <a
                    href={`/deals/${f.deal_id}`}
                    className="text-xs text-ntr-primary hover:underline"
                  >
                    Открыть сделку →
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
